import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  useTheme,
} from "@mui/material";
import React, { ChangeEvent, useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useMutation } from "react-query";
import { useRouter } from "next/router";
import { Trans, useTranslation } from "react-i18next";
import { AppInput, AppInputPassword } from "../share/app-input";
import AppButton from "../share/app-button";
import { AppRegex } from "../../utils/regex";
import UserManagementService from "../../service/user-management.service";
import { EAPP_ROUTE } from "../../enums/route.enum";
import MuiPhoneNumber from "material-ui-phone-number";
import { capitalize } from "../../utils/capitalize";
import GoogleCaptcha from "../share/google-captcha";

export const SignUpForm: React.FC<{}> = () => {
  const theme = useTheme();
  const userManagementService = new UserManagementService();
  const { t } = useTranslation();
  const router = useRouter();
  const query = router.query;
  const [formErrors, setFormErrors] = useState<string>("");
  //const [captcha, setFormErrors] = useState<string>("");
  const [isCheckedCondition, setIsCheckedCondition] = useState<boolean>(false);
  const [formDirtyFields, setFormDirtyFields] = useState<any>({});
  const [captchaRef, setCaptchaRef] = useState<any>(null);
  const [captchaValue, setCaptchaValue] = useState<any>(null);
  //const captchaRef = useRef<any>(null)

  useEffect(() => {
    query &&
      query.invited &&
      query.email &&
      formik.setFieldValue("email", (query?.email as string).replace(" ", "+"));
  }, [query]);

  const formik = useFormik({
    initialValues: {
      fullName: "",
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      phoneNumber: "",
      phoneAreaCode: "+46", // Defaults to SE
      confirmPassword: "",
    },
    validationSchema: Yup.object().shape({
      fullName: Yup.string().required(
        t("sign_up_page__error_message__full_name_is_required")
      ),
      firstName: Yup.string()
        .required(t("sign_up_page__error_message__first_name_is_required"))
        .matches(
          AppRegex.nameValidation,
          t("sign_up_page__error_message__first_name_is_not_valid")
        ),
      lastName: Yup.string()
        .required(t("sign_up_page__error_message__last_name_is_required"))
        .matches(
          AppRegex.nameValidation,
          t("sign_up_page__error_message__last_name_is_not_valid")
        ),
      email: Yup.string()
        .required(t("sign_up_page__error_message__email_is_required"))
        .matches(
          AppRegex.email,
          t("sign_up_page__error_message__email_is_not_valid")
        ),
      password: Yup.string()
        .required(t("sign_up_page__error_message__password_is_required"))
        .matches(
          AppRegex.strongPassword,
          t("sign_up_page__error_message__password_is_not_strong_enough")
        ),
      phoneNumber: Yup.string()
        .required(t("sign_up_page__error_message__phone_number_is_required"))
        .matches(
          AppRegex.phone,
          t("sign_up_page__error_message__phone_number_is_not_valid")
        ),

      phoneAreaCode: Yup.string()
        .required(t("sign_up_page__error_message__phone_area_code_is_required"))
        .matches(
          AppRegex.areaCode,
          t("sign_up_page__error_message__area_code_is_not_valid")
        ),

      confirmPassword: Yup.string().required(
        t("sign_up_page__error_message__confirm_password_is_required")
      ),
    }),
    onSubmit(values) {
      if (values.password === values.confirmPassword) {
        const finalData: Types.SignupRequest = {
          email: values.email,
          fullName: values.fullName,
          password: values.password,
          phoneNumber: `${values.phoneAreaCode} ${values.phoneNumber}`,
          captchaToken: captchaValue,
        };
        captchaRef.current?.reset();
        signUpMutation.mutate(finalData);
      }
    },
  });
  const signUpMutation = useMutation(
    async (userData: Types.SignupRequest) => {
      setFormErrors("");
      const { data, hasError, error }: Types.RequestResponse<null> =
        await userManagementService.signup(userData);

      if (hasError) {
        if (error === 409) {
          setFormErrors(t("sign_up_page__error_message__user_already_exists"));

          throw new Error("");
        } else if (error === 400 || error === 403) {
          setFormErrors(t("sign_up_page__error_message__form_error"));
          throw new Error("");
        } else {
          setFormErrors("");
        }
      }

      return data;
    },
    {
      onSuccess: () => {
        router.push(
          `${EAPP_ROUTE.CONFIRM_EMAIL}?email=${encodeURIComponent(
            formik.values.email
          )}`
        );
      },
    }
  );
  const inputs = [
    {
      label: t("sign_up_page__full_name"),
      name: "fullName",
      autoFocus: false,
      type: "text",
    },
    {
      label: t("sign_up_page__email"),
      name: "email",
      autoFocus: false,
      type: "email",
    },
    {
      label: t("sign_up_page__password"),
      name: "password",
      autoFocus: false,
      type: "password",
    },
    {
      label: t("sign_up_page__confirm_password"),
      name: "confirmPassword",
      autoFocus: false,
      type: "password",
    },
    {
      label: t("sign_up_page__phone_number"),
      name: "phoneNumber",
      autoFocus: false,
      type: "number",
    },
  ];
  const handleFormikChange = (e: any) => {
    if (formDirtyFields[e.target.name] === undefined) {
      setFormDirtyFields({ ...formDirtyFields, [e.target.name]: true });
    }

    formik.handleChange(e);
  };
  const renderInput = (input) => {
    return input.name === "password" || input.name === "confirmPassword" ? (
      <AppInputPassword
        key={input.name}
        variant="outlined"
        autoFocus={input.autoFocus}
        disabled={
          input.name === "email" && query.invited && query.email ? true : false
        }
        error={
          input.name === "confirmPassword"
            ? Boolean(
                (formDirtyFields["confirmPassword"] &&
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword) ||
                  (formDirtyFields["confirmPassword"] &&
                    formik.touched.confirmPassword &&
                    formik.values.confirmPassword !== formik.values.password)
              )
            : Boolean(
                formDirtyFields[input.name] &&
                  (formik.touched as any)[input.name] &&
                  (formik.errors as any)[input.name]
              )
        }
        fullWidth
        helperText={
          input.name === "confirmPassword"
            ? formDirtyFields["confirmPassword"] &&
              formik.errors.confirmPassword &&
              formik.touched.confirmPassword
              ? formik.errors.confirmPassword
              : formik.touched.confirmPassword &&
                formik.values.confirmPassword !== formik.values.password
              ? t("account_page__error_message__confirm_password_is_not_valid")
              : ""
            : formDirtyFields["confirmPassword"] &&
              (formik.touched as any)[input.name] &&
              (formik.errors as any)[input.name]
        }
        label={input.label}
        margin="normal"
        name={input.name}
        onChange={handleFormikChange}
        onBlur={formik.handleBlur}
        type={input.type}
        value={(formik.values as any)[input.name]}
      />
    ) : (
      <AppInput
        key={input.name}
        variant="outlined"
        disabled={
          input.name === "email" && query.invited && query.email ? true : false
        }
        autoFocus={input.autoFocus}
        error={Boolean(
          formDirtyFields[input.name] &&
            (formik.touched as any)[input.name] &&
            (formik.errors as any)[input.name]
        )}
        fullWidth
        helperText={
          formDirtyFields[input.name] &&
          (formik.touched as any)[input.name] &&
          (formik.errors as any)[input.name]
        }
        label={input.label}
        margin="normal"
        name={input.name}
        onChange={handleFormikChange}
        onBlur={formik.handleBlur}
        type={input.type}
        value={(formik.values as any)[input.name]}
      />
    );
  };

  const handleCheckCondition = (e: ChangeEvent<HTMLInputElement>) => {
    setIsCheckedCondition(e.target.checked);
  };
  const handleFirstNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("firstName", e.target.value);
    if (formik.values.lastName !== "") {
      formik.setFieldValue(
        "fullName",
        `${capitalize(e.target.value)} ${capitalize(formik.values.lastName)}`
      );
    } else {
      formik.setFieldValue("fullName", `${capitalize(e.target.value)}`);
    }
    handleFormikChange(e);
  };
  const handleLastNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("lastName", e.target.value);
    if (formik.values.firstName !== "") {
      formik.setFieldValue(
        "fullName",
        `${capitalize(formik.values.firstName)} ${capitalize(e.target.value)}`
      );
    } else {
      formik.setFieldValue("fullName", `${capitalize(e.target.value)}`);
    }
    handleFormikChange(e);
  };

  return (
    <form
      noValidate
      onSubmit={formik.handleSubmit}
      onChange={() => captchaRef?.current?.execute()}
    >
      {inputs.map((input) =>
        input.name === "fullName" ? (
          <Box sx={{ display: "flex" }} key={`${input.name}-wrapper`}>
            <AppInput
              key={"firstName"}
              variant="outlined"
              autoFocus={true}
              error={Boolean(
                formDirtyFields["firstName"] &&
                  (formik.touched as any)["firstName"] &&
                  (formik.errors as any)["firstName"]
              )}
              fullWidth
              helperText={
                formDirtyFields["firstName"] &&
                (formik.touched as any)["firstName"] &&
                (formik.errors as any)["firstName"]
              }
              label={"First Name"}
              margin="normal"
              name={"firstName"}
              onChange={handleFirstNameChange}
              onBlur={formik.handleBlur}
              type={input.type}
              value={(formik.values as any)["firstName"]}
              sx={{
                mr: "0.8rem",
              }}
            />
            <AppInput
              key={"lastName"}
              variant="outlined"
              error={Boolean(
                formDirtyFields["lastName"] &&
                  (formik.touched as any)["lastName"] &&
                  (formik.errors as any)["lastName"]
              )}
              fullWidth
              helperText={
                formDirtyFields["lastName"] &&
                (formik.touched as any)["lastName"] &&
                (formik.errors as any)["lastName"]
              }
              label={"Last Name"}
              margin="normal"
              name={"lastName"}
              onChange={handleLastNameChange}
              onBlur={formik.handleBlur}
              type={input.type}
              value={(formik.values as any)["lastName"]}
            />
          </Box>
        ) : input.name === "phoneNumber" ? (
          <Box sx={{ display: "flex" }} key={`${input.name}-wrapper`}>
            <MuiPhoneNumber
              variant="outlined"
              label={t("sign_up_page__phone_area_code")}
              value={formik.values.phoneAreaCode}
              name="phoneAreaCode"
              size="medium"
              sx={{
                width: "18rem",
                mr: "0.8rem",
                mt: "1.6rem",
                mb: "0.8rem",
              }}
              inputProps={{ maxLength: 5 }}
              preferredCountries={["se"]}
              error={Boolean(
                formDirtyFields["phoneAreaCode"] &&
                  formik.touched.phoneAreaCode &&
                  formik.errors.phoneAreaCode
              )}
              helperText={
                formDirtyFields["phoneAreaCode"] &&
                formik.touched.phoneAreaCode &&
                formik.errors.phoneAreaCode
              }
              onChange={(e) => formik.setFieldValue("phoneAreaCode", e)}
            />
            {renderInput(input)}
          </Box>
        ) : (
          renderInput(input)
        )
      )}

      <Box
        sx={{
          color: "text.primary",
          mt: 3,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              onChange={handleCheckCondition}
              data-testid="signup-checkbox"
            />
          }
          label={
            <Trans
              data-testid="I-read-the-condition"
              i18nKey={"sign_up_page__I_have_read_the_terms_and_conditions"}
              components={[
                <a
                  style={{
                    color: theme.palette.primary.dark,
                    textDecoration: "underline",
                  }}
                  href="https://parsly.com/en/terms-and-conditions/"
                  data-testid="regulation-link"
                ></a>,
              ]}
            />
          }
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <GoogleCaptcha
          handleValue={setCaptchaValue}
          handleRef={setCaptchaRef}
        />
      </Box>
      {formErrors && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="error">
            <Box>
              <b>{formErrors}</b>
            </Box>
          </Alert>
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        <AppButton
          disabled={
            !isCheckedCondition ||
            (!formik.isValid && formik.dirty) ||
            !formik.dirty ||
            !captchaRef?.current?.getValue()
          }
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          data-testid="sign_up_page__register"
          loading={signUpMutation.isLoading}
        >
          {t("sign_up_page__register")}
        </AppButton>
      </Box>
      <Box sx={{ mt: 2 }}></Box>
    </form>
  );
};
